import {getFormValues, formValueSelector} from 'redux-form';
import {List} from 'immutable';
import classNames from 'classnames';
import i18next from 'i18next';

import createSelector from 'web-app/util/custom-create-selector';
import * as DraftSelectors from 'web-app/react/entities/drafts/selectors';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import {ObservationVariant} from 'web-app/react/entities/observations/constants';
import {getAmbiguousVariants} from 'web-app/react/entities/observations/helpers';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import ObservationBehaviors from 'web-app/behaviors/observation-behavior-ids';
import * as AssessmentHelpers from 'web-app/react/feed/post/new-post/new-assessment/helpers';
import {collectFrameworkIDsFromObservationBehaviors} from 'web-app/react/entities/observations/typed-helpers';
import {type RootState} from 'web-app/react/redux/main-reducer';
import {ObservationVersion} from 'web-app/util/graphql/api-types';

import {getPostTypeLabel} from './helpers';
import * as NewPostConstant from '../post/new-post/constants';
import * as Constants from './constants';

export const makeIsObservationMode = () =>
    createSelector(
        (state: RootState) => state.feed.draftManager.showObservationForm,
        show => show,
    );

const newPostFormValues = (state: RootState, props: {formName: string}) => getFormValues(props.formName)(state) || {};

export const makeIsEdit = () =>
    createSelector(
        (state: RootState, props: {postId?: string}) => props.postId,
        postId => {
            return Boolean(postId);
        },
    );

export const makeFormName = () =>
    createSelector(makeIsEdit(), makeIsObservationMode(), (isEdit, isObservationMode) => {
        if (isObservationMode) {
            return isEdit ? NewPostConstant.EDIT_OBSERVATION_FORM_NAME : NewPostConstant.OBSERVATION_FORM_NAME;
        } else {
            return isEdit ? NewPostConstant.EDIT_FORM_NAME : NewPostConstant.FORM_NAME;
        }
    });

const formSelector = (state: RootState, props: {postId?: string}) => {
    const formIdent = makeFormName()(state, props);
    return formValueSelector(formIdent);
};
export const makeFormVariant = () => (state: RootState, props: {postId?: string}) =>
    formSelector(state, props)(state, 'variant');

const newPostFiles = (state: RootState, props: {formName: string}) =>
    state.fileUpload.files.get(props.formName) || List();
export const makeGetCurrentDraftId = () => (state: RootState, props: {postId?: string}) => {
    return state.feed.draftManager.currentDraftId || props.postId;
};

export const makeActiveObservationVariant = () =>
    createSelector(
        (state: RootState) => state.feed.draftManager.observationVariant,
        (state: RootState, props: {observationVariant?: string}) => props && props.observationVariant,
        makeFormVariant(),
        (observationVariantFromState, observationVariantFromProps, formVariant) =>
            formVariant || observationVariantFromState || observationVariantFromProps,
    );

export const makeGetDrafts = () =>
    createSelector(
        DraftSelectors.getDrafts,
        makeIsObservationMode(),
        makeGetCurrentDraftId(),
        (drafts, isObservationMode, draftId) => {
            // Unless we're editing, we filter out drafts with sentAt which indicates they've already been sent.
            // This was done in an attempt to fix https://famlyapp.atlassian.net/browse/PTP-828
            if (draftId) {
                return drafts.filter(draft => draft.isObservation === isObservationMode);
            }
            return drafts.filter(draft => draft.isObservation === isObservationMode && !draft.sentAt);
        },
    );

export const makeDraft = () =>
    createSelector(makeGetDrafts(), makeGetCurrentDraftId(), (drafts, draftId) => {
        return drafts.find(draft => draft.postId === draftId);
    });

const draftBehaviors = createSelector(
    makeDraft(),
    (state: RootState, props: {behaviors?: BehaviorsCollection}) => (props ? props.behaviors : null),
    (draft, behaviors) => {
        if (behaviors) {
            return behaviors;
        }

        return draft ? draft.behaviors : new BehaviorsCollection([]);
    },
);

export const makeDraftPostBehaviors = () =>
    createSelector(makeDraft(), draft => {
        return draft ? draft.postBehaviors : new BehaviorsCollection([]);
    });

export const makeCanSendPosts = () =>
    createSelector(
        (state: RootState, props: {disablePosts?: boolean}) => props.disablePosts,
        AccountSelectors.canSendPosts,
        (disablePosts, canGenerallySendPosts) => canGenerallySendPosts && !disablePosts,
    );

export const makeShowNewObservation = () =>
    createSelector(
        (state: RootState, props: {disableObservations?: boolean}) => props.disableObservations,
        AccountSelectors.showNewObservation,
        draftBehaviors,
        (disableObservations, canGenerallySendObservations, behaviors) =>
            !disableObservations &&
            (behaviors.includes(ObservationBehaviors.CanRequestReview) || canGenerallySendObservations),
    );

const persistedNewPostFiles = createSelector(newPostFiles, newPostFiles =>
    newPostFiles.filter(f => f.uploadProgress === null),
);

export const makeCanSendObservation = () =>
    createSelector(
        draftBehaviors,
        makeIsEdit(),
        (state: RootState, props: {canSendObservation?: boolean}) => props.canSendObservation,
        (behaviors, isEdit, canSendObservation) => {
            if (canSendObservation) {
                return canSendObservation;
            }
            return isEdit
                ? behaviors.includes(ObservationBehaviors.CanEditObservation)
                : behaviors.includes(ObservationBehaviors.CanSendObservation);
        },
    );

export const makeAmbiguousVariants = () => createSelector(draftBehaviors, behaviors => getAmbiguousVariants(behaviors));

export const makeFrameworkBehavior = () =>
    createSelector(draftBehaviors, behaviors => behaviors.behaviorForId(ObservationBehaviors.CanLinkToFrameworks));

export const makeFrameworkIdsToFetch = () =>
    createSelector(draftBehaviors, draftBehaviors => collectFrameworkIDsFromObservationBehaviors(draftBehaviors));

export const makeCanRequestReview = () =>
    createSelector(draftBehaviors, behaviors => behaviors.includes(ObservationBehaviors.CanRequestReview));

export const makeCanAddNextSteps = () =>
    createSelector(draftBehaviors, behaviors => behaviors.includes(ObservationBehaviors.CanAddNextSteps));

export const getCurrentDraft = createSelector(
    makeIsObservationMode(),
    makeGetCurrentDraftId(),
    newPostFormValues,
    persistedNewPostFiles,
    makeActiveObservationVariant(),
    (isObservation, draftId, newPostFormValues: any, persistedNewPostFiles, variantFromState) => {
        const {
            selectedRecipients,
            statements,
            areaRefinements,
            nextStep,
            from,
            variant,
            observedAt,
            ...otherNewPostValues
        } = newPostFormValues;
        const otherFormValueKeys = Object.keys(otherNewPostValues);

        if (
            draftId ||
            otherFormValueKeys.length ||
            (statements && statements.length) ||
            (areaRefinements && areaRefinements.length) ||
            (isObservation && selectedRecipients) ||
            (persistedNewPostFiles && persistedNewPostFiles.size)
        ) {
            let observationData;

            if (isObservation) {
                observationData = {
                    nextStep,
                    observedAt,
                    statements,
                    areaRefinements,
                    variant: variantFromState || variant,
                };

                if (
                    (variant === ObservationVariant.ASSESSMENT || variant === ObservationVariant.TWO_YEAR_PROGRESS) &&
                    areaRefinements
                ) {
                    observationData.areaRefinements = AssessmentHelpers.prepareAreaRefinementsForApi(areaRefinements);
                }
            }

            return {
                isObservation,
                draftId,
                from,
                observationData,
                recipients: selectedRecipients,
                ...otherNewPostValues,
                files: persistedNewPostFiles,
            };
        }

        return null;
    },
);

export const makePostTypeOptions = () =>
    createSelector(
        makeCanSendPosts(),
        makeShowNewObservation(),
        (state: RootState, props: {observationVersion?: ObservationVersion}) => props.observationVersion,
        (canSendPosts, showNewObservation, observationVersion) =>
            [
                canSendPosts
                    ? {
                          id: Constants.PostTypeItem.post,
                          elementId: 'selectPost',
                          className: 'feed_draft_inlineTabsOption',
                          title: getPostTypeLabel(i18next.t('feed.post')),
                      }
                    : null,
                showNewObservation && observationVersion === ObservationVersion.REGULAR
                    ? {
                          id: Constants.PostTypeItem.observation,
                          elementId: 'selectObservation',
                          className: classNames('feed_draft_inlineTabsOption', 'feed_draft_observationOption'),
                          title: getPostTypeLabel(i18next.t('feed.observation')),
                      }
                    : null,
            ].filter(a => a),
    );
